import { INTERNAL_QUESTION_ID_PREFIX, STANDARD_CHECK_DEFINITIONS } from 'ComponentLibrary/src/playground/tools/PdfValidator/constants'
import {
    QuestionItem,
    QuestionStatus,
    ValidationQuestion,
    ValidationQuestionDefinition,
} from 'ComponentLibrary/src/playground/tools/PdfValidator/types'

const createInitialQuestions = <T>(): QuestionItem<T>[] => []

const createQuestionsFromDefinitions = <T>(definitions: ValidationQuestionDefinition[]): QuestionItem<T>[] =>
    definitions.map((question) => ({
        ...question,
        result: null,
        isPending: false,
    }))

const toTrimmedString = (value: unknown): string | null => {
    if (typeof value !== 'string') {
        return null
    }
    const trimmed = value.trim()
    return trimmed.length > 0 ? trimmed : null
}

const isObject = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null

export const answerLabel = (status: QuestionStatus): string => {
    if (status === 'yes') {
        return 'Ja'
    }
    if (status === 'no') {
        return 'Nein'
    }
    if (status === 'text_answer') {
        return 'Textantwort'
    }
    return 'Unklar'
}

export const answerChipColor = (status: QuestionStatus): 'green' | 'red' | 'orange' | 'grey' => {
    if (status === 'yes') {
        return 'green'
    }
    if (status === 'no') {
        return 'red'
    }
    if (status === 'text_answer') {
        return 'grey'
    }
    return 'orange'
}

export const createStandardChecks = (
    definitions: ReadonlyArray<ValidationQuestionDefinition> = STANDARD_CHECK_DEFINITIONS
): ValidationQuestion[] =>
    definitions.map((question) => ({
        ...question,
        result: null,
        isPending: false,
    }))

const createNumberedId = (prefix: string, existingIds: Set<string>): string => {
    let nextIndex = 1
    let candidate = `${prefix}${nextIndex}`

    while (existingIds.has(candidate)) {
        nextIndex += 1
        candidate = `${prefix}${nextIndex}`
    }

    return candidate
}

export const createQuestionId = (existingIds: Set<string>): string => createNumberedId(INTERNAL_QUESTION_ID_PREFIX, existingIds)

const normalizeQuestionDefinitions = (value: unknown): ValidationQuestionDefinition[] => {
    if (!Array.isArray(value)) {
        return []
    }

    const existingIds = new Set<string>()
    const result: ValidationQuestionDefinition[] = []

    value.forEach((item) => {
        if (!isObject(item)) {
            return
        }

        const question = toTrimmedString(item.question)
        if (!question) {
            return
        }

        const id = createQuestionId(existingIds)
        existingIds.add(id)
        result.push({ id, question })
    })

    return result
}

const normalizeQuestionKey = (question: string): string => question.trim().replace(/\s+/g, ' ').toLowerCase()

export const loadPersistedQuestions = <T>(storageKeys: string | string[]): QuestionItem<T>[] => {
    if (typeof window === 'undefined') {
        return createInitialQuestions<T>()
    }

    try {
        const keys = Array.isArray(storageKeys) ? storageKeys : [storageKeys]
        const allDefinitions: ValidationQuestionDefinition[] = []
        const existingIds = new Set<string>()
        const seenQuestionKeys = new Set<string>()

        keys.forEach((storageKey) => {
            const rawValue = window.localStorage.getItem(storageKey)
            if (!rawValue) {
                return
            }

            const parsed = JSON.parse(rawValue) as unknown
            const definitions = normalizeQuestionDefinitions(parsed)
            definitions.forEach((definition) => {
                const normalizedQuestionKey = normalizeQuestionKey(definition.question)
                if (seenQuestionKeys.has(normalizedQuestionKey)) {
                    return
                }

                const id = createQuestionId(existingIds)
                existingIds.add(id)
                seenQuestionKeys.add(normalizedQuestionKey)
                allDefinitions.push({
                    id,
                    question: definition.question,
                })
            })
        })

        if (allDefinitions.length === 0) {
            return []
        }
        return createQuestionsFromDefinitions<T>(allDefinitions)
    } catch {
        return []
    }
}
